/**
 * 從 card slug 萃取顯示元資料（Major Arcana 編號 + 視覺類別 key）。
 *
 * 用於 v2 TarotCard 的 SVG 抽象視覺：在 Imagen 真圖到位前，
 * 不再用 placeholder gradient 色塊，改畫對應的幾何 motif。
 */

/** Major Arcana 22 張（0–XXI）— 順序對應 Rider-Waite 標準排序。 */
export const MAJOR_ARCANA_NUM: Record<string, number> = {
  'the-fool': 0,
  'the-magician': 1,
  'the-high-priestess': 2,
  'the-empress': 3,
  'the-emperor': 4,
  'the-hierophant': 5,
  'the-lovers': 6,
  'the-chariot': 7,
  strength: 8,
  'the-hermit': 9,
  'wheel-of-fortune': 10,
  justice: 11,
  'the-hanged-man': 12,
  death: 13,
  temperance: 14,
  'the-devil': 15,
  'the-tower': 16,
  'the-star': 17,
  'the-moon': 18,
  'the-sun': 19,
  judgement: 20,
  'the-world': 21,
};

/**
 * SVG art family — 把 22 張 Major Arcana 歸到 6 個視覺類型。
 * 每個 theme 都會為這 6 個 family 各畫一個 motif。
 */
export type CardArtFamily =
  | 'tower'    // 結構 / 突破 — Tower, Death, Devil, Hanged Man
  | 'hermit'   // 內省 / 引導 — Hermit, High Priestess, Hierophant, Justice
  | 'star'     // 希望 / 光 — Star, Sun, Moon, Judgement, Temperance, Strength
  | 'wheel'    // 變化 / 循環 — Wheel of Fortune, World, Chariot, Magician
  | 'figure'   // 人 / 關係 — Empress, Emperor, Lovers, Fool
  | 'default'; // unknown

const FAMILY_BY_SLUG: Record<string, CardArtFamily> = {
  'the-tower': 'tower',
  death: 'tower',
  'the-devil': 'tower',
  'the-hanged-man': 'tower',
  'the-hermit': 'hermit',
  'the-high-priestess': 'hermit',
  'the-hierophant': 'hermit',
  justice: 'hermit',
  'the-star': 'star',
  'the-sun': 'star',
  'the-moon': 'star',
  judgement: 'star',
  temperance: 'star',
  strength: 'star',
  'wheel-of-fortune': 'wheel',
  'the-world': 'wheel',
  'the-chariot': 'wheel',
  'the-magician': 'wheel',
  'the-empress': 'figure',
  'the-emperor': 'figure',
  'the-lovers': 'figure',
  'the-fool': 'figure',
};

export function cardArtFamily(slug: string): CardArtFamily {
  return FAMILY_BY_SLUG[slug] ?? 'default';
}

/** Roman numeral 0–XXI（給 mystic theme 用）。 */
export function toRoman(n: number): string {
  if (n === 0) return '0';
  const map: Array<[string, number]> = [
    ['XX', 20],
    ['X', 10],
    ['IX', 9],
    ['V', 5],
    ['IV', 4],
    ['I', 1],
  ];
  let out = '';
  let x = n;
  for (const [s, v] of map) {
    while (x >= v) {
      out += s;
      x -= v;
    }
  }
  return out;
}

/** 取 slug 對應的 Major Arcana 編號（不在表內 → null，用於 minor arcana 之後擴充）。 */
export function cardNumber(slug: string): number | null {
  const n = MAJOR_ARCANA_NUM[slug];
  return n === undefined ? null : n;
}
